import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import StatusBadge from "../../components/leaves/StatusBadge";
import {
  ArrowLeft,
  AlertTriangle,
  Calendar,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { LEAVE_TYPE_LABEL } from "../../lib/leaveOptions";
import { formatISTDate, formatISTDateTime } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";
import { toast } from "sonner";

export default function LeaveDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [leave, setLeave] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/leaves/${id}`)
      .then(({ data }) => !cancelled && setLeave(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function cancelLeave() {
    try {
      await api.put(`/leaves/${id}/cancel`);
      toast.success("Leave cancelled");
      navigate("/leaves", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-callisto-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading leave…
      </div>
    );
  }

  if (error || !leave) {
    return (
      <div className="bg-white border border-callisto rounded-xl p-8 text-center">
        <h2 className="text-lg font-bold text-callisto-navy">Leave unavailable</h2>
        <p className="mt-2 text-sm text-callisto-muted">{error || "Not found"}</p>
        <Link
          to="/leaves"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-callisto-gold hover:underline"
        >
          <ArrowLeft size={14} /> Back to leaves
        </Link>
      </div>
    );
  }

  const isOwner = user?.role === "sales_officer" && leave.sales_officer_id === user.id;
  const canCancel = isOwner && (leave.status === "pending" || leave.status === "approved");

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="leave-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link
          to="/leaves"
          className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
        >
          <ArrowLeft size={14} />
          Back to leaves
        </Link>
        {canCancel && (
          <Button
            variant="outline"
            onClick={cancelLeave}
            className="h-10 border-red-200 text-red-700 hover:bg-red-50"
            data-testid="leave-cancel-button"
          >
            Cancel leave
          </Button>
        )}
      </div>

      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold text-callisto-navy">
            {LEAVE_TYPE_LABEL[leave.leave_type]} Leave
          </h1>
          <StatusBadge status={leave.status} />
          {leave.is_lop && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5">
              <AlertTriangle size={11} />
              {plural(leave.lop_days, "LOP day")}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-callisto-muted flex items-center gap-1.5">
          <Calendar size={14} />
          {/* The span, in IST prose. `joinParts` puts the separator BETWEEN
              things that exist — a half day is often absent and the old
              template carried its "·" either way. */}
          <span data-testid="leave-detail-dates">
            {joinParts([
              leave.from_date === leave.to_date
                ? formatISTDate(leave.from_date)
                : `${formatISTDate(leave.from_date)} → ${formatISTDate(leave.to_date)}`,
              leave.half_day
                ? leave.half_day === "first_half"
                  ? "1st half"
                  : "2nd half"
                : null,
            ])}
          </span>
          <span className="mx-1.5">·</span>
          <span className="font-semibold text-callisto-navy" data-testid="leave-detail-days">
            {plural(leave.days_count, "day")}
          </span>
        </p>
        <p className="mt-1 text-[11px] uppercase tracking-wider text-callisto-muted">
          Applied by{" "}
          <span className="font-semibold text-callisto-body">
            {leave.sales_officer_name}
          </span>
        </p>
      </div>

      <div className="bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-5">
        <Section title="Reason" Icon={MessageSquare}>
          <p className="text-sm text-callisto-body whitespace-pre-wrap">{leave.reason}</p>
        </Section>

        {/* The two named signatures (B2): verification and final approval are
            different acts by different people, and the detail page says which
            was which. */}
        {leave.verified_by_name && (
          <Section title="Verified by (stage 1)">
            <p
              className="text-sm font-semibold text-callisto-navy"
              data-testid="leave-detail-verified-by"
            >
              {leave.verified_by_name}
            </p>
          </Section>
        )}
        {leave.approver_name && (
          <Section
            title={
              leave.status === "rejected"
                ? "Rejected by"
                : leave.verified_by_name
                  ? "Approved by (stage 2, final)"
                  : "Approved by (final)"
            }
          >
            <p className="text-sm font-semibold text-callisto-navy">
              {leave.approver_name}
            </p>
            {/* ONE remark treatment, shared with the expense detail page: the
                remark sits under the name that signed it, with no heading of
                its own. A "Rejection remark" title restates the badge and the
                section heading directly above it — the remark's job is to say
                what neither of those already says. */}
            {leave.approver_remark && (
              <p
                className="mt-1 text-sm text-callisto-body whitespace-pre-wrap"
                data-testid="leave-detail-remark"
              >
                {leave.approver_remark}
              </p>
            )}
          </Section>
        )}

        {/* THE AUDIT TRAIL. These are the entries that prove who decided what
            and when, so the zone label matters here more than anywhere:
            `toLocaleString()` rendered the viewer's zone with nothing saying
            so, and a decision stamped 00:30 IST read as the previous evening
            on a phone set to another zone. `formatISTDateTime` names it. */}
        <Section title="Timeline">
          <ul className="text-sm text-callisto-body space-y-1" data-testid="leave-detail-timeline">
            <li>
              <span className="text-callisto-muted">Applied at:</span>{" "}
              {formatISTDateTime(leave.applied_at)}
            </li>
            {leave.verified_at && (
              <li>
                <span className="text-callisto-muted">Verified at (stage 1):</span>{" "}
                {formatISTDateTime(leave.verified_at)}
              </li>
            )}
            {leave.decided_at && (
              <li>
                <span className="text-callisto-muted">
                  {leave.status === "rejected" ? "Rejected at:" : "Approved at (final):"}
                </span>{" "}
                {formatISTDateTime(leave.decided_at)}
              </li>
            )}
            {leave.cancelled_at && (
              <li>
                <span className="text-callisto-muted">Cancelled at:</span>{" "}
                {formatISTDateTime(leave.cancelled_at)}
              </li>
            )}
          </ul>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-2 flex items-center gap-1.5">
        {Icon && <Icon size={12} />}
        {title}
      </p>
      {children}
    </div>
  );
}
